/**
 * components/ItemPickerModal.js
 * Search pop-up for picking items into the Transformasi lines. Multi-select (tick several →
 * "Tambah (N)") or single-select (row click confirms, used by a line's "ganti"). Rows show
 * name + active-bucket stock (0-stock dimmed) and the code being searched by. No hard walls:
 * every item stays selectable; the Semua/SET/Spare filter is optional. Filters in place —
 * the caller passes an already smart-sorted list, so order is preserved.
 *
 * Rendered rows are capped at 100 (`filtered.slice(0, 100)`) — filtering/sorting still runs
 * over the FULL list, only the DOM row count is capped, so a real-catalog-scale list (2,000+
 * items) doesn't render thousands of rows on open. `selected` is a Set of internalCodes (not
 * row indices), so ticking a row, then typing a query that filters it out of view, then
 * clearing the query again correctly still shows it ticked.
 */
import React, { useMemo, useState } from "react";
import { fmtQty } from "../utils/idGenerators";
import { bucketLabel } from "../utils/transformations";
import { parseMarketCodes, marketCodesToText } from "../utils/marketCodes";
import { useModalEsc, backdropClose } from "../utils/modalStack";

export default function ItemPickerModal({ items, bucket, multi = true, allowCreateSet = false, onCreateSet, onConfirm, onClose, hideTypeFilter = false }) {
  const [query, setQuery] = useState("");
  const [searchBy, setSearchBy] = useState("semua");
  const [typeFilter, setTypeFilter] = useState("all"); // all | set | spare
  const [selected, setSelected] = useState(() => new Set());

  useModalEsc(onClose);

  const stockField = bucket === "CABANG" ? "stokCabang" : "stokPusat";

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const fieldsFor = (it) => {
      switch (searchBy) {
        case "nama": return [it.name];
        case "kodePasar": return parseMarketCodes(it.marketCodes ?? it.marketCode);
        case "kodeInternal": return [it.internalCode];
        default: return [it.name, ...parseMarketCodes(it.marketCodes ?? it.marketCode), it.internalCode];
      }
    };
    return items.filter((it) => {
      if (typeFilter === "set" && it.isSet !== true) return false;
      if (typeFilter === "spare" && it.isSet === true) return false;
      if (!needle) return true;
      return fieldsFor(it).filter(Boolean).some((f) => f.toLowerCase().includes(needle));
    });
  }, [items, query, searchBy, typeFilter]);

  const toggle = (code) => setSelected((s) => {
    const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n;
  });
  const codeShown = (it) =>
    searchBy === "kodePasar" ? marketCodesToText(it.marketCodes || []) : (searchBy === "nama" ? null : it.internalCode);
  const rowClick = (it) => { if (multi) toggle(it.internalCode); else onConfirm([it.internalCode]); };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
      <div className="modal-box modal-box--wide" style={{ position: "relative" }} onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Tutup">×</button>
        <h2 className="modal-title" style={{ marginBottom: "var(--gap-lg)" }}>Pilih Barang</h2>

        <div className="picker__search">
          <select className="form-select picker__select" value={searchBy} onChange={(e) => setSearchBy(e.target.value)}>
            <option value="semua">Semua</option>
            <option value="nama">Nama</option>
            <option value="kodePasar">Kode Pasar</option>
            <option value="kodeInternal">Kode Internal</option>
          </select>
          <input className="form-input grow" autoFocus value={query}
            placeholder={{
              semua: "Cari nama, kode pasar, atau kode internal…",
              nama: "Cari nama…",
              kodePasar: "Cari kode pasar…",
              kodeInternal: "Cari kode internal…",
            }[searchBy]}
            onChange={(e) => setQuery(e.target.value)} />
        </div>

        {!hideTypeFilter && (
          <div className="seg picker__filters">
            <button type="button" className={typeFilter === "all" ? "active" : ""} onClick={() => setTypeFilter("all")}>Semua</button>
            <button type="button" className={typeFilter === "set" ? "active" : ""} onClick={() => setTypeFilter("set")}>SET</button>
            <button type="button" className={typeFilter === "spare" ? "active" : ""} onClick={() => setTypeFilter("spare")}>Spare</button>
          </div>
        )}

        <div className="picker__list">
          {filtered.length === 0 && !(allowCreateSet && query.trim()) && (
            <div className="combobox__empty">Tidak ada hasil.</div>
          )}
          {filtered.slice(0, 100).map((it) => {
            const stock = Number(it[stockField]) || 0;
            const code = codeShown(it);
            const meta = [code, `Stok ${bucketLabel(bucket)}: ${fmtQty(stock)}`].filter(Boolean).join(" · ");
            return (
              <div key={it.internalCode}
                className={"picker__row" + (stock === 0 ? " picker__row--dim" : "")}
                onClick={() => rowClick(it)}>
                {multi && (
                  <input type="checkbox" className="picker__cb" checked={selected.has(it.internalCode)}
                    onChange={() => toggle(it.internalCode)} onClick={(e) => e.stopPropagation()} />
                )}
                <div className="picker__name">
                  {it.name}
                  {it.isSet === true && <span className="chip chip--set" style={{ marginLeft: "var(--gap-sm)" }}>SET</span>}
                </div>
                <div className="picker__meta">{meta}</div>
              </div>
            );
          })}
          {allowCreateSet && query.trim() && (
            <div className="picker__row" onClick={() => onCreateSet(query)}>
              <div className="picker__name combobox__create">+ Buat SET baru: "{query.trim()}"</div>
            </div>
          )}
          {filtered.length > 100 && (
            <div className="form-hint" style={{ padding: "8px 4px" }}>
              Menampilkan 100 dari {fmtQty(filtered.length)} — ketik untuk mempersempit.
            </div>
          )}
        </div>

        <div className="picker__foot">
          <div className="picker__count">{multi ? `${selected.size} dipilih` : ""}</div>
          <div style={{ display: "flex", gap: "var(--gap-md)" }}>
            <button type="button" className="btn" onClick={onClose}>Batal</button>
            {multi && (
              <button type="button" className="btn btn-primary" disabled={selected.size === 0}
                onClick={() => onConfirm([...selected])}>Tambah ({selected.size})</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
